'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Calendar,
  Home,
  Inbox,
  Menu,
  Store,
  Ticket,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';
import { cn, isRouteActive } from '@kit/ui/utils';

import type { Brand } from '~/lib/brand';
import type { UserWorkspace } from '../_lib/server/load-user-workspace';
import { HomeMobileNavigation } from './home-mobile-navigation';

const tabClass = (active: boolean) =>
  cn(
    'flex h-full w-full flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors',
    active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
  );

function TabIcon({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon className="size-5" aria-hidden />;
}

/**
 * Fixed bottom tab bar — the mobile navigation surface (monday.com / Etsy
 * pattern): Home · Marketplace · Opportunities · Inbox · More. Search moved
 * to the top row (2026-07-06); "More" opens the full menu sheet
 * (HomeMobileNavigation with a custom trigger). Desktop (md+) hides the bar —
 * the sidebar and the GlobalSearchTopBar own navigation there.
 *
 * Mount it as a sibling of the page content together with a `h-14 md:hidden`
 * spacer so the fixed bar never covers the last rows of content.
 */
export function MobileTabBar(props: {
  workspace: UserWorkspace;
  brand?: Brand;
  /** Explicit `lang` cookie choice from the server layout; `null` = Automatic. */
  selectedLocale: string | null;
}) {
  const pathname = usePathname();

  // Opportunities is a Ballee concept (/auditions); the Flow shell's
  // equivalent pipeline surface is Events.
  const opportunities =
    props.brand === 'ballee'
      ? {
          path: '/auditions',
          icon: Ticket,
          i18nKey: 'common:routes.opportunities',
          defaults: 'Opportunities',
        }
      : {
          path: '/home/events',
          icon: Calendar,
          i18nKey: 'common:routes.events',
          defaults: 'Events',
        };

  return (
    <nav
      className={cn(
        'border-border/60 bg-background/95 supports-[backdrop-filter]:bg-background/80 fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur md:hidden',
        'pb-[env(safe-area-inset-bottom)]',
      )}
      data-testid="mobile-tab-bar"
    >
      <div className="grid h-14 grid-cols-5">
        <Link
          href="/home"
          className={tabClass(isRouteActive('/home', pathname, true))}
        >
          <TabIcon icon={Home} />
          <Trans i18nKey="common:routes.home" defaults="Home" />
        </Link>

        <Link
          href="/marketplace"
          className={tabClass(isRouteActive('/marketplace', pathname, false))}
        >
          <TabIcon icon={Store} />
          <Trans i18nKey="common:routes.marketplace" defaults="Marketplace" />
        </Link>

        <Link
          href={opportunities.path}
          className={tabClass(
            isRouteActive(opportunities.path, pathname, false),
          )}
        >
          <TabIcon icon={opportunities.icon} />
          <Trans
            i18nKey={opportunities.i18nKey}
            defaults={opportunities.defaults}
          />
        </Link>

        <Link
          href="/home/inbox"
          className={tabClass(isRouteActive('/home/inbox', pathname, false))}
        >
          <TabIcon icon={Inbox} />
          <Trans i18nKey="common:routes.inbox" defaults="Inbox" />
        </Link>

        <HomeMobileNavigation
          workspace={props.workspace}
          brand={props.brand}
          selectedLocale={props.selectedLocale}
          trigger={
            <Button variant="ghost" type="button" className={tabClass(false)}>
              <TabIcon icon={Menu} />
              <Trans i18nKey="common:more" defaults="More" />
            </Button>
          }
        />
      </div>
    </nav>
  );
}
